import { API_BASE_URL } from '../utils/apiBaseUrl';

/**
 * JWT Authentication Service
 * Handles token management, refresh logic, and authentication state
 */

// Token storage keys
const ACCESS_TOKEN_KEY = 'brai_access_token';
const REFRESH_TOKEN_KEY = 'brai_refresh_token';
const TOKEN_EXPIRY_KEY = 'brai_token_expiry';

// Token refresh buffer (refresh 2 minutes before expiry)
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

// Refresh token lock to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  refresh_expires_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  company?: string;
  user_type?: string;
}

export interface UserProfile {
  user_id: string;
  email: string;
  name: string;
  company: string;
  user_type: string;
  roles: string[];
  permissions: string[];
  is_superadmin: boolean;
  created_at?: string;
  last_login_at?: string;
  is_active: boolean;
  terms_accepted_at?: string;
  terms_version?: string;
}

/**
 * Store tokens in localStorage
 */
export function storeTokens(tokens: TokenResponse): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  
  // Calculate and store expiry time
  const expiryTime = Date.now() + (tokens.expires_in * 1000);
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
}

/**
 * Clear all tokens from localStorage
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
}

/**
 * Get the access token
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Get the refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if access token is expired or about to expire
 */
export function isTokenExpired(): boolean {
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryStr) return true;
  
  const expiry = parseInt(expiryStr, 10);
  // Consider expired if within buffer time
  return Date.now() >= (expiry - REFRESH_BUFFER_MS);
}

/**
 * Check if there's a valid session (has tokens)
 */
export function hasValidSession(): boolean {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  return !!(accessToken && refreshToken);
}

/**
 * Login with email and password
 */
export async function login(credentials: LoginRequest): Promise<TokenResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Login failed' }));
    throw new Error(error.detail || 'Login failed');
  }
  
  const tokens: TokenResponse = await response.json();
  storeTokens(tokens);
  return tokens;
}

/**
 * Register a new user
 */
export async function register(data: RegisterRequest): Promise<TokenResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
    throw new Error(error.detail || 'Registration failed');
  }
  
  const tokens: TokenResponse = await response.json();
  storeTokens(tokens);
  return tokens;
}

/**
 * Refresh the access token using the refresh token
 */
export async function refreshAccessToken(): Promise<boolean> {
  // If already refreshing, wait for that promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.log('[Auth] No refresh token available');
    return false;
  }
  
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      console.log('[Auth] Refreshing access token...');
      
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      
      if (!response.ok) {
        console.warn('[Auth] Token refresh failed');
        clearTokens();
        return false;
      }
      
      const tokens: TokenResponse = await response.json();
      storeTokens(tokens);
      console.log('[Auth] Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('[Auth] Token refresh error:', error);
      clearTokens();
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
}

/**
 * Logout the current user
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  const accessToken = getAccessToken();
  
  if (refreshToken && accessToken) {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (error) {
      console.warn('[Auth] Logout request failed:', error);
    }
  }
  
  clearTokens();
}

/**
 * Logout from all devices
 */
export async function logoutAll(): Promise<void> {
  const accessToken = getAccessToken();
  
  if (accessToken) {
    try {
      await fetch(`${API_BASE_URL}/auth/logout-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
    } catch (error) {
      console.warn('[Auth] Logout all request failed:', error);
    }
  }
  
  clearTokens();
}

/**
 * Get the current user's profile
 */
export async function getCurrentUser(): Promise<UserProfile | null> {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return null;
  }
  
  // Check if token needs refresh
  if (isTokenExpired()) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      return null;
    }
  }
  
  const token = getAccessToken();
  if (!token) {
    return null;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        // Try to refresh token
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // Retry with new token
          const newToken = getAccessToken();
          if (newToken) {
            const retryResponse = await fetch(`${API_BASE_URL}/auth/me`, {
              headers: {
                'Authorization': `Bearer ${newToken}`,
              },
            });
            if (retryResponse.ok) {
              return await retryResponse.json();
            }
          }
        }
        clearTokens();
        return null;
      }
      throw new Error('Failed to fetch user profile');
    }
    
    return await response.json();
  } catch (error) {
    console.error('[Auth] Failed to get current user:', error);
    return null;
  }
}

/**
 * Accept the Terms of Service and Privacy Policy on behalf of the current user.
 * Returns the updated UserProfile with terms_accepted_at populated.
 */
export async function acceptTerms(): Promise<UserProfile> {
  const accessToken = getAccessToken();
  if (!accessToken) {
    throw new Error('No access token available');
  }
  if (isTokenExpired()) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw new Error('Session expired. Please log in again.');
    }
  }
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}/auth/accept-terms`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to accept terms');
  }
  return response.json();
}

/**
 * Make an authenticated API request with automatic token refresh
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Check if token needs refresh before making request
  if (isTokenExpired()) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw new Error('Session expired. Please log in again.');
    }
  }
  
  const accessToken = getAccessToken();
  if (!accessToken) {
    throw new Error('No access token available');
  }
  
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);
  headers.set('Content-Type', 'application/json');
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  // If 401, try to refresh and retry
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const newToken = getAccessToken();
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        return fetch(url, {
          ...options,
          headers,
        });
      }
    }
    // Clear tokens and throw error
    clearTokens();
    throw new Error('Session expired. Please log in again.');
  }
  
  return response;
}

/**
 * Setup automatic token refresh interval
 */
let refreshInterval: NodeJS.Timeout | null = null;

export function startTokenRefreshInterval(): void {
  // Clear any existing interval
  stopTokenRefreshInterval();
  
  // Check and refresh every minute
  refreshInterval = setInterval(async () => {
    if (hasValidSession() && isTokenExpired()) {
      console.log('[Auth] Token expiring soon, refreshing...');
      await refreshAccessToken();
    }
  }, 60 * 1000); // Check every minute
}

export function stopTokenRefreshInterval(): void {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}

/**
 * Request a password reset email (unauthenticated)
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to send reset email');
  }

  return response.json();
}

/**
 * Reset password using a token received by email
 */
export async function resetPassword(
  email: string,
  token: string,
  newPassword: string,
): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, token, new_password: newPassword }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to reset password');
  }

  return response.json();
}

// User types (matching backend)
export const UserType = {
  PUBLIC: 'public',
  ORGANIZATION: 'organization',
  AGENCY: 'agency',
  INFLUENCER: 'influencer',
  SUPERVISOR: 'supervisor',
  SUPERADMIN: 'superadmin',
} as const;

export type UserTypeValue = typeof UserType[keyof typeof UserType];

// All available user types
export const ALL_USER_TYPES: UserTypeValue[] = [
  UserType.PUBLIC,
  UserType.ORGANIZATION,
  UserType.AGENCY,
  UserType.INFLUENCER,
  UserType.SUPERVISOR,
  UserType.SUPERADMIN,
];

// User type display names
export const USER_TYPE_LABELS: Record<UserTypeValue, string> = {
  [UserType.PUBLIC]: 'Public User',
  [UserType.ORGANIZATION]: 'Organization',
  [UserType.AGENCY]: 'Agency',
  [UserType.INFLUENCER]: 'Influencer',
  [UserType.SUPERVISOR]: 'Supervisor',
  [UserType.SUPERADMIN]: 'Super Admin',
};

// User type colors for badges
export const USER_TYPE_COLORS: Record<UserTypeValue, string> = {
  [UserType.PUBLIC]: 'default',
  [UserType.ORGANIZATION]: 'blue',
  [UserType.AGENCY]: 'purple',
  [UserType.INFLUENCER]: 'orange',
  [UserType.SUPERVISOR]: 'gold',
  [UserType.SUPERADMIN]: 'red',
};

// Permissions
export const Permissions = {
  // View permissions
  VIEW_DASHBOARD: 'view:dashboard',
  VIEW_PUBLIC_CONTENT: 'view:public_content',
  VIEW_ANALYTICS: 'view:analytics',
  VIEW_CLIENT_REPORTS: 'view:client_reports',
  VIEW_ENGAGEMENT_METRICS: 'view:engagement_metrics',
  
  // Feature permissions
  MANAGE_SUBREDDITS: 'manage:subreddits',
  USE_AI_REPLY_GENERATOR: 'use:ai_reply_generator',
  USE_LLM_GEO_ANALYSIS: 'use:llm_geo_analysis',
  MANAGE_TEAM_MEMBERS: 'manage:team_members',
  MANAGE_CLIENTS: 'manage:clients',
  CREATE_CLIENT_REPORTS: 'create:client_reports',
  MANAGE_CAMPAIGNS: 'manage:campaigns',
  
  // Admin permissions
  ADMIN_MANAGE_USERS: 'admin:manage_users',
  ADMIN_MANAGE_ROLES: 'admin:manage_roles',
  ADMIN_VIEW_ALL_SESSIONS: 'admin:view_all_sessions',
  ADMIN_REVOKE_SESSIONS: 'admin:revoke_sessions',
  ADMIN_VIEW_AUDIT_LOGS: 'admin:view_audit_logs',
  ADMIN_SYSTEM_SETTINGS: 'admin:system_settings',
  ADMIN_IMPERSONATE_USER: 'admin:impersonate_user',
  ADMIN_MANAGE_SUPERVISORS: 'admin:manage_supervisors',

  // Supervisor permissions
  SUPERVISOR_VIEW_DASHBOARD: 'supervisor:view_dashboard',
} as const;

export type PermissionValue = typeof Permissions[keyof typeof Permissions];

